import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { colors, getApiUrl } from '../../utils';
import { notifications, extractApiErrorMessage } from '../../utils';
import {
  Tooltip,
  IconButton,
  Icon,
  Popper,
  Paper,
  ClickAwayListener,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Switch,
  Typography,
  Divider
} from '@mui/material';
import { WebNotificationViewModel } from '../../models/settings';
import { SettingsService } from '../../hooks/SettingsService';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { Loader } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import Pagination from '@mui/material/Pagination';
import { FOCUS_DOCUMENT_PARAM } from '../FarmDocuments/component';

/**
 * The document id out of an export notification's `info`, or null for every other notification.
 *
 * The six producers of `info` share no contract - a confirmed supply order writes `{ orderId: ... }`, some
 * write nothing at all - so anything that is not an `exportDocument` payload, JSON or otherwise, reads as
 * nowhere to go rather than as an error.
 */
const readExportDocumentId = (info?: string | null): string | null => {
  if (!info) {
    return null;
  }

  try {
    const payload = JSON.parse(info);

    return payload?.type === 'exportDocument' && payload?.id ? String(payload.id) : null;
  } catch {
    return null;
  }
};

const NotificationsPanel: React.FC<any> = () => {
  const settingsService = new SettingsService();
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const navigate = useNavigate();

  const notificationsDisplayCount = 10;
  const [displayBadgeCount, setDisplayBadgeCount] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [webNotifications, setWebNotifications] = React.useState([] as WebNotificationViewModel[]);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const webNotificationsRef = React.useRef(webNotifications);
  webNotificationsRef.current = webNotifications;

  const buildHubConnection = () => {
    const apiUrl = getApiUrl();

    return new HubConnectionBuilder()
      .withUrl(`${apiUrl}/hubs/notifications`, { accessTokenFactory: () => state.token })
      .withAutomaticReconnect()
      .build();
  };

  const [connection] = React.useState<HubConnection>(() => buildHubConnection());
  const [online, setOnline] = React.useState(false);

  const startConnection = () => {
    connection
      .start()
      .then(() => {
        notifications.info(t('f-hubs-notif-con'));
        setOnline(true);
      })
      .catch((ex) => {
        console.log('Notifications Hub connection failed: ', ex);
        setOnline(false);
      });
  };

  React.useEffect(() => {
    if (connection) {
      startConnection();

      connection.on('ReceiveNotification', receiveNotificationHandler);

      connection.onclose(() => {
        notifications.info(t('f-hubs-notif-discon'));
        setOnline(false);
      });

      connection.onreconnected(() => {
        notifications.info(t('f-hubs-notif-con'));
        setOnline(true);
      });
    }
  }, [connection]);

  const receiveNotificationHandler = (notification: WebNotificationViewModel) => {
    const documentId = readExportDocumentId(notification.info);

    // The toast is what the user actually sees when an export finishes - the menu entry is only found later,
    // by someone who went looking. Both lead to the document.
    if (documentId) {
      notifications.infoAction(notification.message, () => handleMessageClick(notification, documentId));
    } else {
      notifications.info(notification.message);
    }

    const array = [notification, ...webNotificationsRef.current];
    setWebNotifications(array);
  };

  const toggleConnection = () => {
    if (connection.connectionId) {
      connection.stop().then(() => {
        setOnline(false);
      });
      setWebNotifications([]);
    } else {
      startConnection();
      setLoaded(false);
    }
  };

  React.useEffect(() => {
    if (!loaded) {
      loadNotifications();
    }
  }, [loaded]);

  // display not viewed count
  React.useEffect(() => {
    if (webNotifications) {
      const notViewed = webNotifications.filter((x) => !x.isViewed);
      setDisplayBadgeCount(notViewed.length);
    }
  }, [webNotifications]);

  const loadNotifications = async () => {
    setIsBusy(true);

    await settingsService
      .getWebNotifications(page, notificationsDisplayCount)
      .then((result) => {
        setWebNotifications(result.data);
        setTotal(result.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoaded(true);
        setIsBusy(false);
      });
  };

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const renderEmptyListItem = () => {
    return (
      <ListItem key={'empty-list-item'}>
        <ListItemIcon>
          <Icon>{'notifications_off_outlined'}</Icon>
        </ListItemIcon>
        <ListItemText primary={t('f-notifications-empty')} />
      </ListItem>
    );
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const renderFooterListItem = () => {
    if (!webNotifications.length) {
      return null;
    }

    return (
      <>
        <Divider />
        <div className="flex justify-center px-4 py-6">
          <Pagination
            count={Math.ceil(total / notificationsDisplayCount)}
            showFirstButton
            showLastButton
            page={page}
            size="small"
            color="primary"
            onChange={handleChangePage}
            disabled={isBusy}
          />
        </div>
      </>
    );
  };

  const renderLoader = () => {
    return (
      <div className="flex justify-center px-4 py-6">
        <Loader isLoading={isBusy} size={LoaderSize.MEDIUM} />
      </div>
    );
  };

  const handleViewClick = (row: WebNotificationViewModel) => {
    markNotificationsViewed([row.id]);
  };

  const handleMessageClick = async (row: WebNotificationViewModel, documentId: string) => {
    setOpen(false);

    // Following the link is as much an acknowledgement as pressing the bell, so the notification is marked
    // viewed on the way out - quietly, because the navigation is the feedback. A failure there leaves the
    // badge one too high and is deliberately not allowed to hold up the navigation the user asked for.
    if (!row.isViewed) {
      await settingsService.viewNotifications([row.id]).catch(() => undefined);
    }

    navigate(`/farm/documents?${FOCUS_DOCUMENT_PARAM}=${documentId}`);
  };

  const markNotificationsViewed = async (ids: string[]) => {
    await settingsService
      .viewNotifications(ids)
      .then(() => {
        notifications.success(t('f-notifications-marked'));
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const renderHeader = () => {
    return (
      <>
        <div className="flex items-center justify-between p-4">
          <Tooltip arrow title={online ? t('f-com-online') : t('f-com-offline')}>
            <Icon style={{ color: online ? colors.green : colors.red }}>{online ? 'wifi' : 'wifi_off'}</Icon>
          </Tooltip>

          <Typography>{t('f-real-time')}</Typography>

          <Tooltip arrow title={online ? t('f-com-disable') : t('f-com-enable')}>
            <Switch edge="end" color="primary" onChange={toggleConnection} checked={online} />
          </Tooltip>
        </div>
        <Divider />
      </>
    );
  };

  const renderNotificationsList = () => {
    return (
      <List>
        {isBusy && renderLoader()}

        {loaded &&
          webNotifications.map((row) => {
            const documentId = readExportDocumentId(row.info);

            return (
              <ListItem key={row.id} divider>
                <div className="w-full">
                  <div className="flex items-center justify-between">
                    <div>
                      {row.isViewed ? (
                        <>
                          <IconButton aria-label="check" color="default" disabled size="small">
                            <Icon>{'notifications_none'}</Icon>
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Tooltip arrow title={t('f-notifications-mark')}>
                            <IconButton
                              aria-label="check"
                              size="small"
                              onClick={() => handleViewClick(row)}
                              color="primary"
                            >
                              <Icon>{'notifications_active'}</Icon>
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </div>

                    <div>
                      <Typography variant={'caption'}>{new Date(row.dateTime).toLocaleString()}</Typography>
                    </div>
                  </div>

                  <div className="my-2">
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: 'pre-line' }}
                      // Only the notifications that carry somewhere to go look like links - the rest stay inert text.
                      sx={
                        documentId
                          ? { cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }
                          : undefined
                      }
                      onClick={documentId ? () => handleMessageClick(row, documentId) : undefined}
                    >
                      {row.message}
                    </Typography>
                  </div>
                </div>
              </ListItem>
            );
          })}

        {!webNotifications.length && renderEmptyListItem()}
      </List>
    );
  };

  return (
    <>
      <Tooltip arrow title={t('f-notifications')}>
        <Badge badgeContent={displayBadgeCount} overlap="circular" color="secondary">
          <IconButton ref={anchorRef} onClick={handleToggle} color="inherit">
            <Icon>{'notifications'}</Icon>
          </IconButton>
        </Badge>
      </Tooltip>

      <Popper
        key={'header-notifications-panel'}
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper sx={{ minWidth: 360, maxWidth: 360 }} elevation={5}>
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  {renderHeader()}
                  <div className="max-h-[400px] overflow-auto">{renderNotificationsList()}</div>

                  {renderFooterListItem()}
                </div>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default NotificationsPanel;
